import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ticket, Sun, Sprout, Bike, Handshake, Heart, Laptop, Tv, Share2 } from 'lucide-react';
import PrizeCard from '../components/PrizeCard';
import CountdownTimer from '../components/CountdownTimer';
import PartnersBanner from '../components/PartnersBanner';
import Newsletter from '../components/Newsletter';
import ProgressBar from '../components/ProgressBar';
import SocialShareButtons from '../components/SocialShareButtons';
import HeroSlider from '../components/HeroSlider';
import { Button } from '../components/ui/button';

const Landing = () => {
  const { t, i18n } = useTranslation();

  const prizes = [
    {
      title: t('prizes.house.title'),
      description: t('prizes.house.description'),
      value: t('prizes.house.value'),
      imageUrl: '/maison-edenlife.jpeg',
      color: 'bg-secondary',
      icon: Sun,
    },
    {
      title: t('prizes.tractor.title'),
      description: t('prizes.tractor.description'),
      value: t('prizes.tractor.value'),
      imageUrl: '/tracteur-agricole.png',
      color: 'bg-accent',
      icon: Sprout,
    },
    {
      title: t('prizes.moto.title'),
      description: t('prizes.moto.description'),
      value: t('prizes.moto.value'),
      imageUrl: '/tricycle-cargo.jpeg',
      color: 'bg-red-600',
      icon: Bike,
    },
    {
      title: t('prizes.laptop.title'),
      description: t('prizes.laptop.description'),
      value: t('prizes.laptop.value'),
      imageUrl: '/laptop-hp-envy.png',
      color: 'bg-blue-600',
      icon: Laptop,
    },
    {
      title: t('prizes.tv.title'),
      description: t('prizes.tv.description'),
      value: t('prizes.tv.value'),
      imageUrl: '/tv-lg-oled.jpg',
      color: 'bg-purple-600',
      icon: Tv,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }} data-testid="landing-page">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Hero Slider Background */}
        <HeroSlider />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-24" data-testid="hero-section">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl sm:text-6xl md:text-8xl font-bold text-white mb-6 drop-shadow-2xl"
            style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 4px 8px rgba(0,0,0,0.5)' }}
            data-testid="hero-title"
          >
            {t('hero.title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl sm:text-2xl md:text-3xl text-white mb-4 font-semibold drop-shadow-lg"
            style={{ textShadow: '1px 2px 4px rgba(0,0,0,0.5)' }}
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base sm:text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto drop-shadow-md"
            style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.4)' }}
            data-testid="hero-description"
          >
            {t('hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex gap-6 justify-center flex-wrap"
          >
            <Link to="/purchase">
              <Button
                size="lg"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-2xl hover:shadow-3xl rounded-full px-10 py-6 text-xl font-bold"
                data-testid="hero-buy-button"
              >
                <Ticket className="w-6 h-6 mr-3" />
                {t('hero.buyNow')}
              </Button>
            </Link>
          </motion.div>

          <CountdownTimer />
        </div>
      </section>

      {/* Progress Bar */}
      <ProgressBar />

      <section className="py-24 px-6" data-testid="prizes-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-5xl md:text-6xl font-bold text-center text-primary mb-16"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('prizes.title')}
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {prizes.map((prize, index) => (
              <PrizeCard key={index} {...prize} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-primary/5" data-testid="about-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('about.title')}
          </h2>
          <p className="text-xl text-gray-700 mb-6 leading-relaxed">{t('about.mission')}</p>
          <p className="text-lg text-gray-600 leading-relaxed">{t('about.impact')}</p>
        </div>
      </section>

      {/* Call to Action - Partners & Donate */}
      <section className="py-16 px-6 bg-gradient-to-br from-primary to-accent">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Partners CTA */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center text-white"
            >
              <Handshake className="w-16 h-16 mx-auto mb-4 opacity-90" />
              <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('partners.title')}
              </h3>
              <p className="text-white/80 mb-6">
                {t('partners.subtitle')}
              </p>
              <Link to="/partners">
                <Button className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-3 text-lg font-semibold">
                  <Handshake className="w-5 h-5 mr-2" />
                  {t('partners.title')}
                </Button>
              </Link>
            </motion.div>

            {/* Donate CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-md rounded-3xl p-8 text-center text-white"
            >
              <Heart className="w-16 h-16 mx-auto mb-4 opacity-90" />
              <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('donate.title')}
              </h3>
              <p className="text-white/80 mb-6">
                {t('donate.subtitle')}
              </p>
              <Link to="/donate">
                <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full px-8 py-3 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  {t('donate.donateNow')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Share Section */}
      <section className="py-12 px-6 bg-gradient-to-r from-purple-600 via-pink-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Share2 className="w-10 h-10 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              {t('share.helpUsReach')}
            </h2>
            <p className="text-white/80 mb-6 text-base max-w-xl mx-auto">
              {t('share.shareDescription')}
            </p>
            <div className="flex justify-center">
              <SocialShareButtons 
                title="Marathon de l'Avenir EDENLIFE 2026 - Gagnez une maison de $450,000 USD!"
                description="Participez au Marathon de l'Avenir EDENLIFE 2026 et tentez de gagner l'un des 5 prix extraordinaires. Soutenez le développement durable en Haïti!"
                size="lg"
                showLabel={false}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />

      {/* Partners Banner */}
      <PartnersBanner />

    </div>
  );
};

export default Landing;